import {useCallback, useEffect, useState} from 'react'
import {RefreshCw} from 'lucide-react'
import {dispararSync, getSyncStatus, SyncRun} from '../api'
import {useEscopo} from '../ServiceLevelContext'
import {hasServiceLevelPermission} from '../permissions'
import SyncHistory from '../components/SyncHistory'
import {fmtDataHoraFoto} from '../utils/format'
import SyncProgress, {ManualSyncAttempt} from '../components/SyncProgress'

const STATUS: Record<string, string> = {
  OK: 'Concluída',
  RUNNING: 'Em andamento',
  ERRO: 'Erro',
  ERROR: 'Erro',
  FAILED: 'Erro',
}

export default function AdminHub() {
  const podeSincronizar = hasServiceLevelPermission(useEscopo(), 'sync')
  const [status, setStatus] = useState<{ts_foto: string | null; runs: SyncRun[]} | null>(null)
  const [sincronizando, setSincronizando] = useState(false)
  const [manualAttempt, setManualAttempt] = useState<ManualSyncAttempt | null>(null)
  const [msg, setMsg] = useState('')
  const [erro, setErro] = useState('')
  const load = useCallback(async () => {
    if (!podeSincronizar) return
    try {
      setStatus(await getSyncStatus())
      setErro('')
    } catch (error) {
      setErro(
        error instanceof Error ? error.message : 'Não foi possível consultar a sincronização.'
      )
    }
  }, [podeSincronizar])
  useEffect(() => {
    if (podeSincronizar) load()
  }, [load, podeSincronizar])
  const ultima = status?.runs?.[0]
  const rodando = ultima?.status === 'RUNNING' || sincronizando
  useEffect(() => {
    if (!podeSincronizar || !rodando) return
    const timer = window.setInterval(load, 15000)
    return () => window.clearInterval(timer)
  }, [load, rodando, podeSincronizar])

  const sincronizar = async () => {
    if (!podeSincronizar || rodando) return
    setSincronizando(true)
    const startedAt = Date.now()
    setManualAttempt({startedAt, outcome: 'RUNNING'})
    setErro('')
    setMsg('')
    try {
      const result = await dispararSync()
      setManualAttempt({startedAt, finishedAt: Date.now(), outcome: result.ok ? 'OK' : 'ERRO'})
      setMsg(result.ok ? `Sincronização concluída: ${result.detalhe ?? 'dados atualizados.'}` : '')
      if (!result.ok) setErro(result.erro || 'Falha ao sincronizar os dados.')
      await load()
      if (!result.ok) setErro(result.erro || 'Falha ao sincronizar os dados.')
    } catch (error) {
      setManualAttempt({startedAt, finishedAt: Date.now(), outcome: 'ERRO'})
      setMsg('')
      setErro(error instanceof Error ? error.message : 'Falha ao sincronizar.')
    } finally {
      setSincronizando(false)
    }
  }

  return (
    <>
      <div className='page-header'>
        <div>
          <h1 className='flex items-center gap-2'>
            <RefreshCw size={24} /> Sincronização
          </h1>
          <div className='sub'>
            Mantenha os dados da operação atualizados e acompanhe as últimas sincronizações.
          </div>
        </div>
      </div>
      {podeSincronizar && (
        <section className='card sync-card' aria-labelledby='sync-title'>
          <h2 id='sync-title' className='flex items-center gap-2'>
            <RefreshCw size={17} /> Sincronização de dados
          </h2>
          <p className='chart-note'>Atualize a base do Service Level com os dados do Databricks.</p>
          <div className='pedido-header'>
            <div className='field'>
              <div className='fl'>Última atualização</div>
              <div className='fv'>{status ? fmtDataHoraFoto(status.ts_foto) : 'Consultando…'}</div>
            </div>
            <div className='field'>
              <div className='fl'>Última execução</div>
              <div className='fv'>
                {ultima ? (
                  <span
                    className={`badge ${
                      ultima.status === 'OK'
                        ? 'FINALIZADO'
                        : ultima.status === 'RUNNING'
                        ? 'ENVIADO'
                        : 'p3'
                    }`}
                  >
                    {STATUS[ultima.status] ?? ultima.status}
                  </span>
                ) : (
                  'Sem execução registrada'
                )}
              </div>
            </div>
            <div className='field'>
              <div className='fl'>Detalhe</div>
              <div className='fv sync-detail'>{ultima?.detalhe ?? '—'}</div>
            </div>
          </div>
          <button
            type='button'
            className='btn'
            data-analytics-feature='Nível de Serviço · Sincronizar agora'
            onClick={sincronizar}
            disabled={rodando || !status}
          >
            <RefreshCw size={16} className={rodando ? 'animate-spin' : ''} />
            {rodando ? 'Sincronizando…' : 'Sincronizar agora'}
          </button>
          <SyncProgress
            running={rodando}
            latest={ultima}
            runs={status?.runs ?? []}
            manualAttempt={manualAttempt}
          />
          {msg && (
            <p className='feedback success' role='status'>
              {msg}
            </p>
          )}
          {erro && (
            <div className='feedback error' role='alert'>
              {erro}
              <button type='button' className='btn secondary' onClick={load}>
                Consultar novamente
              </button>
            </div>
          )}
        </section>
      )}
      {podeSincronizar && (
        <SyncHistory runs={status?.runs ?? null} error={!!erro} onRefresh={load} />
      )}
    </>
  )
}
